import _ from "lodash";
import React from "react";
import showdown from "showdown";

// Constants
import {
  ANALYSING,
  GENERATING,
  THINKING,
  SEARCHING,
  WRITING,
  BROWSING_THE_INTERNET,
  GENERATING_EVALUATION,
} from "Constants/stringConstants";
import { REGEX_PATTERNS } from "./Constants";

// Utils
import { getNestedChildren } from "Utils/EntityFlowUtils";
import { htmlToText, getStyleStrippedText } from "Utils";
import { getIsAIEditDeleteButtonEnabled } from "Utils/ACLStoreUtils";

// Design system
import {
  CopyOutlined,
  DeleteOutlined,
  PencilOutlined,
} from "@toddle-edu/ds-icons";
import { colors } from "@toddle-edu/ds-theme";

export const getAIText = ({ t, currentAIState, customLoadingTextRenderer }) => {
  switch (currentAIState) {
    case ANALYSING:
      return t("ai:analysing_label");
    case GENERATING:
    case THINKING:
    case WRITING:
      return t("ai:thinking");
    case BROWSING_THE_INTERNET:
      return t("ai:browsing_the_internet");
    case SEARCHING:
      return t("ai:browsing_the_internet");
    case GENERATING_EVALUATION:
      return t("ai:evaluating_this_conversation");
    default: {
      if (customLoadingTextRenderer)
        return customLoadingTextRenderer?.(currentAIState);
      return t("classRoom:writing");
    }
  }
};

export const convertMarkdownToHTML = ({
  content,
  shouldParseContent = false,
}) => {
  const converter = new showdown.Converter({
    tables: true,
    // disableForced4SpacesIndentedSublists is used to disable the 4 spaces indentation for lists
    // setting this to true allows us to use 2 spaces indentation for lists
    disableForced4SpacesIndentedSublists: true,
  });

  if (shouldParseContent) {
    const parsedContent = _.replace(content, /###/g, "@@@")?.trim();
    const covertedText = converter.makeHtml(parsedContent);

    return _.replace(covertedText, /@@@/g, "###");
  }

  return converter.makeHtml(content);
};

/**
 * Parses list view content (array of lines) into a flat TextListData array.
 * Each line is processed to remove list markers and extra spaces, converted to HTML using convertMarkdownToHTML,
 * and then assembled into a tree where nesting is determined by the number of leading spaces (2 spaces = one level).
 * Also determines whether the list is ordered or unordered based on its marker.
 *
 * @param {Array<string>} rawContent - Array of list view lines.
 * @returns {Object} Object containing listData (Array) and ordered (boolean)
 */
export const parseListViewContentToTextListData = rawContent => {
  if (!rawContent || rawContent.length === 0)
    return { listData: [], ordered: false };

  // Determine if list is ordered by checking the first non-empty line marker
  let isOrdered = false;
  const firstLineMatch = rawContent[0].match(/^\s*([-\d]+\.?)\s+/);
  if (firstLineMatch) {
    const marker = firstLineMatch[1];
    isOrdered = /^\d+\.?$/.test(marker);
  }

  // Filter out empty lines
  const lines = rawContent.filter(line => line.trim().length > 0);

  const result = [];
  // Root node
  const root = { key: "root", children: [] };
  result.push(root);

  // Stack holds { node, depth, childCount }
  const stack = [{ node: root, depth: -1, childCount: 0 }];

  // For each line, determine depth, clean marker and convert content
  lines.forEach(rawLine => {
    // Determine number of leading spaces
    const leadingSpacesMatch = rawLine.match(/^(\s+)/);
    const spaces = leadingSpacesMatch ? leadingSpacesMatch[1].length : 0;
    // Depth: every 2 spaces increment depth by 1
    const depth = Math.floor(spaces / 2);

    // Remove leading spaces and list marker (-, digit, or digit followed by '.')
    const cleanedLine = rawLine.replace(/^\s*([-\d]+\.?)\s+/, "").trim();
    // Convert any markdown styling to HTML
    const htmlValue = convertMarkdownToHTML({
      content: cleanedLine,
      shouldParseContent: true,
    });

    // Pop stack until top has depth < current depth
    while (stack.length && stack[stack.length - 1].depth >= depth) {
      stack.pop();
    }
    const parentItem = stack[stack.length - 1];
    // Increase parent's childCount and build key using parent's key and letter sequence (a, b, c, ...)
    parentItem.childCount++;
    const letter = String.fromCharCode(96 + parentItem.childCount); // 1 -> a, 2 -> b, etc.
    const newKey =
      parentItem.node.key === "root"
        ? `${parentItem.childCount}-${letter}`
        : `${parentItem.node.key}-${letter}`;

    const newNode = { key: newKey, value: htmlValue, children: [] };
    // Append this node's key to parent's children array
    parentItem.node.children.push(newKey);
    // Add newNode to result
    result.push(newNode);
    // Push new node onto the stack for potential children (childCount starts at 0)
    stack.push({ node: newNode, depth, childCount: 0 });
  });

  const rootItemChildrenCount = _.size(_.get(root, "children", []));

  return {
    listData: result,
    // no point in having an ordered list with only 1 item
    isOrdered: rootItemChildrenCount === 1 ? false : isOrdered,
  };
};

/**
 * Parses accordion-style content and converts it to a structured format for NestedAccordion
 * @param {Array<string>} content - Array of content lines
 * @returns {Object} Object containing parsed items and title
 */
export const parseAccordionContent = content => {
  const lines = _.clone(content);

  const items = [];
  let idCounter = 1,
    lastDepth = 1,
    lastItemType;

  // Maps to track parent-child relationships
  const depthMap = {}; // Maps depth to last item at that depth

  const getAssessmentType = type => {
    const typeMappings = {
      LE: "le",
      "LEARNING ENGAGEMENT": "le",
      "FORMATIVE ASSESSMENT": "fmt",
      "SUMMATIVE ASSESSMENT": "smt",
      AI_TUTOR: "ai_tutor",
      WORKSHEET: "worksheet",
    };

    const defaultType = _.lowerCase(type);
    return _.get(typeMappings, type, defaultType);
  };

  _.forEach(lines, _line => {
    // Skip empty lines
    if (_.isEmpty(_.trim(_line))) return;

    let line = _line;

    // Calculate depth based on relative indentation
    const indentMatch = line.match(REGEX_PATTERNS.INDENTATION);
    const currentIndent = indentMatch ? indentMatch[0].length : 0;

    // Depth 1 for no spaces, otherwise 1 + number of spaces
    let depth = 1;
    if (currentIndent > 0) {
      depth = 1 + currentIndent;

      if (depth > lastDepth) {
        // depth can only be greater by 1 than last depth
        // and that is also only possible when last item is a folder
        if (lastItemType === "FOLDER") {
          depth = lastDepth + 1;
        } else {
          depth = lastDepth;
        }
      }
    }

    lastDepth = depth;

    // remove * from the line
    line = line.replace(/[*]/g, "");

    // remove [ID: <number>] from the line
    line = line.replace(/\[ID:\s*\d+\]/g, "");

    // Extract type from brackets
    const typeMatch = line.match(/\[([A-Z_\s]+)\]/i);
    const type = typeMatch ? _.toUpper(typeMatch[1]) : "FOLDER";

    lastItemType = type;
    const isLink = type === "LINK";

    // Extract label and content
    let label = "";
    let subtext = "";

    const contentAfterType = line.substring(line.indexOf("]") + 1).trim();
    const colonIndex = contentAfterType.lastIndexOf(":");

    if (colonIndex > -1) {
      // extract URL if it's a link
      if (isLink) {
        const urlMatch = contentAfterType.match(/https?:\/\/[^\s]+/);
        const url = urlMatch ? urlMatch[0] : "";

        // Remove the url and any trailing colon or dash (with optional spaces) from the label
        label = _.trim(
          contentAfterType
            .replace(url, "")
            .replace(/[:\-]+\s*$/, "")
            .trim()
        );
        subtext = url;
      } else {
        // otherwise everything before the colon is label and after is subtext
        label = _.trim(contentAfterType.substring(0, colonIndex));
        subtext = _.trim(contentAfterType.substring(colonIndex + 1));
      }
    } else {
      label = _.trim(contentAfterType);
    }

    // Create the item structure
    const id = _.toString(idCounter);
    idCounter++;

    const resourceType =
      type === "FOLDER"
        ? "FOLDER"
        : _.includes(["RESOURCE", "LINK"], type)
        ? "ENTITY_FLOW_RESOURCE"
        : "ASSESSMENT";

    // Build parent relationship
    let parent = null;
    if (depth > 1) {
      const parentDepth = depth - 1;
      if (_.has(depthMap, parentDepth)) {
        parent = { id: depthMap[parentDepth] };
      }
    }

    // Create item based on type
    const node = {
      id,
      label,
      depth,
      resourceType,
      parent,
      isRepeated: false,
      repeatedResources: [],
      childrenV2: [],
      childrenHierarchyInfo:
        resourceType === "FOLDER"
          ? {
              maxDepth: 0,
              totalCount: 0,
              totalCountWithFolder: 0,
            }
          : null,
    };

    // Add additional properties based on type
    if (resourceType === "ASSESSMENT") {
      if (!_.isEmpty(subtext)) {
        // remove anything between [[]] from subtext
        subtext = subtext.replace(/\[\[.*?\]\]/g, "").trim();
        node.subtext = subtext;
      }

      node.item = {
        id: `${id}-item`,
        type: "LEARNING_ENGAGEMENT",
        title: {
          id: `${id}-item-title`,
          value: label,
        },
        academicTerm: {
          id: `${id}-item-academicTerm`,
          lockingStatus: "UNLOCKED",
        },
        assessmentType: {
          id: `${id}-item-assessmentType`,
          value: getAssessmentType(type),
        },
        image: {
          id: `${id}-item-image`,
          value: null,
        },
        category: null,
      };
    } else if (resourceType === "ENTITY_FLOW_RESOURCE") {
      node.resourceType = "ORGANIZATION_RESOURCE";

      if (isLink) {
        const urlMatch = subtext.match(/https?:\/\/[^\s]+/);
        const url = urlMatch ? urlMatch[0] : "";
        const textContent = _.trim(subtext.replace(url, ""));

        node.item = {
          id: `${id}-item`,
          attachment: {
            id: `${id}-item-attachment`,
            type: "LINK",
            attachmentTitle: null,
            name: label,
            mimeType: null,
            url,
            signedUrl: url,
            thumbUrl: null,
          },
        };
        if (!_.isEmpty(textContent)) {
          node.subtext = textContent;
        }
      } else {
        node.item = {
          id: `${id}-item`,
          attachment: {
            id: `${id}-item-attachment`,
            type: "NOTE",
            title: `<b>${label}:</b> ${subtext}`,
            name: null,
            mimeType: null,
            url: null,
            signedUrl: null,
            thumbUrl: null,
          },
        };
      }
    }

    // Add to depthMap for parent-child tracking
    depthMap[depth] = id;

    // If this item has a parent, add it to parent's children
    if (parent) {
      const parentItem = _.find(items, { id: parent.id });

      if (parentItem) {
        parentItem.childrenV2.push({ id });
      }
    }

    items.push(node);
  });

  return items;
};

/**
 * Generates resource relation config for NestedAccordion
 * @param {Array} items - Array of accordion items
 * @returns {Object} Resource relation configuration
 */
export const generateResourceRelationConfig = items => {
  const parentConfig = {};
  const childrenConfig = {};

  _.forEach(items, node => {
    const itemId = node.id;
    parentConfig[itemId] = [];

    // Find all ancestors for this item
    let currentParent = node.parent;
    while (currentParent) {
      parentConfig[itemId].push(currentParent.id);
      const parentNode = _.find(items, { id: currentParent.id });
      currentParent = _.get(parentNode, "parent");
    }

    // For folders, collect all children
    if (node.resourceType === "FOLDER") {
      childrenConfig[itemId] = _.map(
        getNestedChildren({
          items,
          id: itemId,
        }),
        "id"
      );
    }
  });

  return {
    parentConfig,
    childrenConfig,
    resources: items,
  };
};

/**
 * Parses FieldView content and removes UID header
 * @param {Array<string>} content - Array of content lines
 * @returns {Object} Parsed content and field type
 */
export const parseFieldViewContent = content => {
  if (_.isEmpty(content)) return { parsedContent: content, type: "simple" };

  // Remove UID header
  const uidLineIndex = _.findIndex(
    content,
    line => _.includes(line, "**UID:") || _.includes(line, "** UID:")
  );

  const contentWithoutUID =
    uidLineIndex !== -1
      ? [...content.slice(0, uidLineIndex), ...content.slice(uidLineIndex + 1)]
      : [...content];

  // Determine if it's a list - simplify to just check for lines starting with -, *, or number.
  const listItems = _.filter(contentWithoutUID, line =>
    REGEX_PATTERNS.LIST_ITEM.test(line)
  );

  if (_.isEmpty(listItems)) {
    const isCodeBlock = _.startsWith(
      _.trim(_.get(contentWithoutUID, 0, "")),
      "```"
    );
    if (isCodeBlock) {
      return { parsedContent: contentWithoutUID, type: "simple" };
    }

    // Remove various number formats: large numbers, [number], (number), and standalone numbers
    const filteredContent = _.map(contentWithoutUID, line => {
      // Replace numbers larger than 6 digits with empty string
      let filteredLine = line.replace(REGEX_PATTERNS.LARGE_NUMBERS, "");
      // Replace [<number>] format with empty string
      filteredLine = filteredLine.replace(REGEX_PATTERNS.BRACKET_NUMBERS, "");
      // Replace (<number>) format with empty string
      filteredLine = filteredLine.replace(
        REGEX_PATTERNS.PARENTHESIS_NUMBERS,
        ""
      );
      return filteredLine;
    });

    return { parsedContent: filteredContent, type: "simple" };
  }

  // Check for nesting
  const indentations = _.map(listItems, item => {
    const match = item.match(REGEX_PATTERNS.INDENTATION);
    return match ? match[1].length : 0;
  });

  const hasMultipleIndentLevels = _.uniq(indentations).length > 1;

  return {
    parsedContent: contentWithoutUID,
    type: hasMultipleIndentLevels ? "nestedList" : "simpleList",
  };
};

/**
 * Parses list content into a format suitable for TextList component
 * @param {Array<string>} content - Array of content lines
 * @param {boolean} isNested - Whether the list is nested
 */
export const parseListForTextList = (content, isNested = false) => {
  const listItems = [];
  const itemsByIndent = {};
  const topLevelKeys = [];

  let title;

  // First pass: Create items and track their indentation levels
  _.forEach(content, (line, index) => {
    let matched = false;
    let indentLevel = 0;
    let key = `item-${index}`;
    let value = "";

    // Try the comprehensive flexible pattern first
    const flexMatch = line.match(REGEX_PATTERNS.FLEXIBLE_NUMBER_PATTERN);
    if (flexMatch) {
      matched = true;
      const [
        ,
        indentation,
        listMarker,
        textBefore,
        bracketNum,
        parenthesisNum,
        plainNum,
        textAfter,
      ] = flexMatch;
      indentLevel = indentation ? indentation.length : 0;

      // Get the ID from whichever number format was matched
      const id = bracketNum || parenthesisNum || plainNum || `item-${index}`;

      // Combine text before and after the number
      let fullText = textBefore || "";
      if (textAfter) {
        fullText = fullText.trim() + " " + textAfter;
      }

      key = id;
      value = _.trim(fullText);
    }
    // If the flexible pattern didn't match, try the legacy patterns
    else {
      // Try each pattern until one matches
      const listItemPatterns = [
        REGEX_PATTERNS.LIST_ITEM_WITH_BRACKET_NUMBER,
        REGEX_PATTERNS.LIST_ITEM_WITH_PARENTHESIS_NUMBER,
        REGEX_PATTERNS.LIST_ITEM_WITH_TEXT_NUMBER,
      ];

      for (const pattern of listItemPatterns) {
        const match = line.match(pattern);
        if (match) {
          matched = true;
          const [, indentation, listMarker] = match;
          indentLevel = indentation ? indentation.length : 0;

          // Extract the ID and text differently based on pattern
          if (pattern === REGEX_PATTERNS.LIST_ITEM_WITH_BRACKET_NUMBER) {
            // Pattern 1: [number] format
            const [, , , id, text] = match;
            key = id || `item-${index}`;
            value = _.trim(text);
          } else if (
            pattern === REGEX_PATTERNS.LIST_ITEM_WITH_PARENTHESIS_NUMBER
          ) {
            // Pattern 2: (number) format
            const [, , , textBefore, id, textAfter] = match;
            key = id || `item-${index}`;
            value = _.trim(`${textBefore || ""} ${textAfter || ""}`);
          } else if (pattern === REGEX_PATTERNS.LIST_ITEM_WITH_TEXT_NUMBER) {
            // Pattern 3: number in text
            const [, , , textBefore, id, textAfter] = match;
            key = id || `item-${index}`;
            value = _.trim(`${textBefore || ""} ${textAfter || ""}`);
          }
          break;
        }
      }
    }

    // If none of the patterns matched, use a simple check for list items
    if (!matched && line.match(REGEX_PATTERNS.LIST_ITEM)) {
      const indentation = line.match(REGEX_PATTERNS.INDENTATION)[1];
      indentLevel = indentation ? indentation.length : 0;
      value = line.replace(REGEX_PATTERNS.LIST_ITEM, "").trim();
    }

    // Skip if not a list item
    if (!matched && !line.match(REGEX_PATTERNS.LIST_ITEM)) {
      // if it starts with ** it may be a title
      if (line.startsWith("**")) {
        title = line;
      }
      return;
    }

    // Create the list item
    const item = { key, value };

    // Track items by their indentation level
    if (!itemsByIndent[indentLevel]) {
      itemsByIndent[indentLevel] = [];
    }
    itemsByIndent[indentLevel].push(key);

    // Track top level items
    if (indentLevel === 0) {
      topLevelKeys.push(key);
    }

    listItems.push(item);
  });

  // Create root item that contains all top level items
  const rootItem = {
    key: "root",
    value: "",
    children: topLevelKeys,
  };

  // If this is a nested list, we need to establish parent-child relationships
  if (isNested && !_.isEmpty(itemsByIndent)) {
    // Sort indentation levels (0, 2, 4, etc.)
    const indentLevels = _.sortBy(Object.keys(itemsByIndent).map(Number));

    // For each indentation level (except the smallest/top level)
    for (let i = 1; i < indentLevels.length; i++) {
      const currentLevel = indentLevels[i];
      const parentLevel = _.findLast(
        indentLevels,
        level => level < currentLevel
      );

      // If we found a valid parent level
      if (!_.isUndefined(parentLevel)) {
        // For each item at the current indentation level
        _.forEach(itemsByIndent[currentLevel], currentKey => {
          // Find the closest preceding item from the parent level
          const currentItemIndex = _.findIndex(listItems, { key: currentKey });

          // Look backwards through the list to find the nearest parent
          let parentKey = null;
          for (let j = currentItemIndex - 1; j >= 0; j--) {
            const potentialParent = listItems[j];
            if (_.includes(itemsByIndent[parentLevel], potentialParent.key)) {
              parentKey = potentialParent.key;
              break;
            }
          }

          // If we found a parent, add this item as its child
          if (parentKey) {
            const parentItem = _.find(listItems, { key: parentKey });
            if (parentItem) {
              if (!parentItem.children) {
                parentItem.children = [];
              }
              parentItem.children.push(currentKey);
            }
          }
        });
      }
    }
  }

  // Add the root item and then all other items
  return { listItems: [rootItem, ...listItems], title };
};

/**
 * Determines the block type based on content pattern
 * @param {string} content - Line of content to analyze
 * @returns {string} Block type identifier
 */
const getBlockTypeFromContent = content => {
  if (REGEX_PATTERNS.FIELD_VIEW_PATTERN.test(content)) {
    return "FieldView";
  }
  if (REGEX_PATTERNS.ACCORDION_VIEW_PATTERN.test(content)) {
    return "AccordionView";
  }
  if (REGEX_PATTERNS.BANNER_IMAGE_TAG_PATTERN.test(content)) {
    return "BannerImageView";
  }
  if (REGEX_PATTERNS.BANNER_LOADER_PATTERN.test(content)) {
    return "BannerSkeletonLoader";
  }
  if (REGEX_PATTERNS.MARKDOWN_TABLE_PATTERN.test(content)) {
    return "MarkdownView";
  }
  if (REGEX_PATTERNS.HTML_TAG_PATTERN.test(content)) {
    return "HTMLView";
  }
  if (REGEX_PATTERNS.ATTACHMENT_PATTERN.test(content)) {
    return "ATTACHMENTS";
  }
  return "MarkdownView";
};

export const getBlocks = ({ content }) => {
  if (!content) return [];

  const lineSeparatedContent = _.split(content, "\n");
  const blocks = [];

  let lastBlockType, lastBlock, currentBlockType;
  let isInsideCodeBlock = false;
  let codeBuffer = "";
  let isInsideBanner = false;
  let bannerBuffer = "";

  _.forEach(lineSeparatedContent, line => {
    const trimmedLine = _.trim(line);

    //Check for banner image
    if (/<banner_image>/i.test(line)) {
      isInsideBanner = true;
      bannerBuffer = line + "\n";

      // If closing tag is on same line → handle immediately
      if (/<\/banner_image>/i.test(line)) {
        const bannerBlock = {
          type: "BannerImageView",
          content: [bannerBuffer],
        };
        blocks.push(bannerBlock);

        lastBlock = bannerBlock;
        lastBlockType = "BannerImageView";
        isInsideBanner = false;
        bannerBuffer = "";
      }

      return;
    }

    if (isInsideBanner) {
      bannerBuffer += line + "\n";

      if (/<\/banner_image>/i.test(line)) {
        const bannerBlock = {
          type: "BannerImageView",
          content: [bannerBuffer],
        };
        blocks.push(bannerBlock);

        lastBlock = bannerBlock;
        lastBlockType = "BannerImageView";
        isInsideBanner = false;
        bannerBuffer = "";
      }

      return;
    }

    // Check for triple backtick pattern to toggle code block state
    const isTripleBacktick = _.startsWith(trimmedLine, "```");

    if (isTripleBacktick) {
      if (isInsideCodeBlock) {
        // closing ```
        codeBuffer += line + "\n";

        // push whole code block as a single string
        const codeBlock = {
          type: "FieldView",
          content: [codeBuffer],
        };
        blocks.push(codeBlock);

        lastBlock = codeBlock;
        lastBlockType = "FieldView";
        isInsideCodeBlock = false;
        codeBuffer = "";
      } else {
        // opening ```
        isInsideCodeBlock = true;
        codeBuffer = line + "\n"; // includes ```
      }
      return;
    }

    // ---------- inside ```...``` ----------
    if (isInsideCodeBlock) {
      if (_.isEmpty(trimmedLine)) {
        if (lastBlock) {
          lastBlock.content.push(line);
        }
        return;
      }

      const hasHTMLTags = REGEX_PATTERNS.HTML_TAG_PATTERN.test(line);
      currentBlockType = hasHTMLTags ? "HTMLView" : "FieldView";

      codeBuffer += line + "\n";
      return;
    } else {
      // Normal block processing
      currentBlockType = getBlockTypeFromContent(line);
    }

    if (currentBlockType === lastBlockType) {
      lastBlock.content.push(line);
    } else {
      if (_.isEmpty(trimmedLine)) return;

      lastBlock = {
        type: currentBlockType,
        content: [line],
      };
      blocks.push(lastBlock);
      lastBlockType = currentBlockType;
    }
  });

  return blocks;
};

export const getAssistantBlockActions = () => {
  const actions = [];

  actions.push({
    key: "copy",
    icon: <CopyOutlined />,
    tooltipLabel: "common:copy",
  });

  if (getIsAIEditDeleteButtonEnabled()) {
    actions.push(
      {
        key: "internalEdit",
        icon: <PencilOutlined />,
        tooltipLabel: "common:edit",
      },
      {
        key: "delete",
        icon: <DeleteOutlined />,
        tooltipLabel: "common:delete",
      }
    );
  }

  return actions;
};

export const getUserBlockActions = content => {
  const actions = [];

  const isContentEmpty = _.isEmpty(_.trim(content));
  if (!isContentEmpty) {
    actions.push({
      key: "copy",
      icon: <CopyOutlined />,
      tooltipLabel: "common:copy",
    });
  }

  if (getIsAIEditDeleteButtonEnabled()) {
    actions.push(
      {
        key: "internalEdit",
        icon: <PencilOutlined />,
        tooltipLabel: "common:edit",
      },
      // {
      //   key: "edit",
      //   icon: <PencilOutlined />,
      //   tooltipLabel: "common:edit",
      // },
      {
        key: "delete",
        icon: <DeleteOutlined />,
        tooltipLabel: "common:delete",
      }
    );
  }

  return actions;
};

export const getHTMLContentForBlock = params => {
  const { content } = params;

  let convertedText = convertMarkdownToHTML({
    content,
    shouldParseContent: true,
  })?.trim();

  const tableStyle = ' style="background-color: #f5f5ff; text-align: left;"';
  convertedText = _.replace(convertedText, /<th>/g, `<th${tableStyle}>`);
  convertedText = _.replace(
    convertedText,
    /<tr>(\s*?)<td>/g,
    `<tr>$1<td${tableStyle}>`
  );

  const hasDelimeter = _.includes(convertedText, "###");

  if (hasDelimeter) {
    const splitConvertedText = _.split(convertedText, "###");

    if (_.size(splitConvertedText) === 3)
      return _.trim(_.nth(splitConvertedText, 1));
  }

  return _.replace(convertedText, /###/g, "\n");
};

const applyStylesToTable = tableElement => {
  if (!tableElement) return;

  tableElement.style.fontSize = "1.6rem";

  const thead = tableElement.querySelector("thead");
  thead.style.backgroundColor = colors.decorativeSurfaceTwoL;
  thead.style.color = colors.textDefault;
  thead.style.minHeight = "32px";

  const thCells = tableElement.querySelectorAll("th");
  thCells.forEach(th => {
    th.style.padding = "8px";
    th.style.textAlign = "left";
    th.style.overflow = "hidden";
    th.style.border = `1px solid ${colors.borderSubtle}`;
    th.style.fontSize = "1.2rem";
  });

  const trCells = tableElement.querySelectorAll("tr td:first-child");
  trCells.forEach(td => {
    td.style.backgroundColor = colors.decorativeSurfaceTwoL;
  });

  const tbody = tableElement.querySelector("tbody");
  tbody.style.backgroundColor = colors.surfaceWhite;
  tbody.style.color = colors.textDefault;

  const tdCells = tableElement.querySelectorAll("td");
  tdCells.forEach(td => {
    td.style.padding = "8px";
    td.style.overflow = "hidden";
    td.style.border = `1px solid ${colors.borderSubtle}`;
    td.style.fontSize = "1.2rem";
  });

  return tableElement;
};

export const replaceTablesWithLinks = async params => {
  const { content, captureAndUploadImage, assessmentToolType } = params;

  if (!captureAndUploadImage || assessmentToolType) return content;

  const convertedText = convertMarkdownToHTML({
    content,
    shouldParseContent: true,
  })?.trim();

  const tempElement = document.createElement("div");
  tempElement.innerHTML = convertedText;

  document.body.appendChild(tempElement);

  // Get all tables in the temporary element
  const tables = tempElement.getElementsByTagName("table");

  // Replace each table with the plain text "image link"
  for (let i = tables.length - 1; i >= 0; i--) {
    const table = applyStylesToTable(tables[i]);
    const url = await captureAndUploadImage(table);

    if (url) {
      const imgTag = document.createElement("img");
      imgTag.src = url;
      imgTag.alt = url;
      imgTag.class = "blobImg";
      tables[i].parentNode.replaceChild(imgTag, tables[i]);
    }
  }

  const modifiedHtmlString = tempElement.innerHTML;

  // Remove the tempElement from the document
  document.body.removeChild(tempElement);

  return modifiedHtmlString;
};

export const getTextValueForBlock = async params => {
  const {
    key,
    content,
    fieldType,
    captureAndUploadImage,
    assessmentToolType,
  } = params;

  let convertedText = convertMarkdownToHTML({
    content,
    shouldParseContent: true,
  })?.trim();

  if (fieldType === "RichText") {
    if (captureAndUploadImage) {
      convertedText = await replaceTablesWithLinks({
        content,
        captureAndUploadImage,
        assessmentToolType,
      });
    }

    if (key === "openingMessage") {
      convertedText = content;
    } else {
      convertedText = getStyleStrippedText({
        text: convertedText,
        type: "RICH_TEXT_EDITOR",
      });
    }

    // Adding 'tde-paragraph' class to <p> tags to remove default top/bottom margins
    // When content is dynamically added to Rich Text Editor (unlike user typing),
    // we need to manually add this class that RTE would normally apply automatically
    convertedText = _.replace(
      convertedText,
      /<p>/g,
      '<p class="tde-paragraph">'
    );
  } else {
    convertedText = htmlToText(convertedText, {
      wordwrap: null,
    })?.trim();
  }

  const hasDelimeter = _.includes(convertedText, "###");

  if (hasDelimeter) {
    const splitConvertedText = _.split(convertedText, "###");

    if (_.size(splitConvertedText) === 3)
      return _.trim(_.nth(splitConvertedText, 1));
  }

  return _.replace(convertedText, /###/g, "\n");
};

// for RTE, we use heading from level 3 to 5
// so we need to replace h1 with h3, h2 with h4 and h3 with h5
const replaceHTMLHeadings = htmlString => {
  if (!htmlString) return htmlString;

  const mapping = { 1: "3", 2: "4", 3: "5" };
  // Match both opening and closing tags of h1, h2, h3
  return htmlString.replace(
    /<(\/?)h([1-3])([^>]*)>/gi,
    (match, slash, level, rest) => {
      const newLevel = mapping[level];
      return `<${slash}h${newLevel}${rest}>`;
    }
  );
};

export const getTextForClipboard = ({ content }) => {
  const htmlText = convertMarkdownToHTML({ content });

  const plainText = htmlToText(htmlText, {
    wordwrap: null,
  })?.trim();

  return { htmlText: replaceHTMLHeadings(htmlText), plainText };
};

export const escapeMarkdownInUserMessage = content => {
  if (!content || typeof content !== "string") return content;

  let sanitizedContent = content;

  // escape asterisks everywhere in messages
  sanitizedContent = sanitizedContent.replace(/\*/g, "\\*");

  // escape blockquote only at line start
  sanitizedContent = sanitizedContent.replace(/^(\s*)>/gm, "$1\\>");

  return sanitizedContent;
};
